import { useMemo, useState } from "react";
import { DatePicker, Select, message } from "antd";
import dayjs from "dayjs";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { Button } from "V2.0/common/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "V2.0/common/dialog";
import * as campaignService from "V2.0/services/campaignService";
import { showErrorToast } from "V2.0/utils";

// Campaigns older than this are no longer on the active database, so the report can
// only reach back this far. The backend enforces the same limit.
const REPORT_LOOKBACK_DAYS = 90;

// Final-outcome statuses from the campaigns page filter (CAMPAIGN_STATUS in ../SingleCampaigns).
// Scheduled / Ongoing / Queued / CallPutOnHold are left out: they are in-progress states a
// campaign passes through, never the outcome of a finished call. Declared here because
// SingleCampaigns imports this component, so importing back would be circular. The backend
// accepts the same list (CAMPAIGN_REPORT_STATUSES in routes/api/campaign.ts).
// "Delivered" and "Rejected" are dropped here (vs. Frontend's version) — they are email-only
// outcomes and this whitelabel build has no Email campaign channel.
const REPORT_STATUS_OPTIONS = [
  "Completed",
  "Failed",
  "Unanswered",
  "Voicemail",
  "Blocked",
].map((status) => ({ label: status, value: status }));

// Values match CAMPAIGN_REPORT_CHANNEL_SOURCES in the backend's campaignReportService.
// "Email" is dropped here (vs. Frontend's version) — no Email campaign channel in this build.
const REPORT_CHANNEL_OPTIONS = [
  { label: "Voice", value: "voice" },
  { label: "SMS", value: "sms" },
  { label: "WhatsApp", value: "whatsapp" },
];

// Assistant types that produce campaigns — same whitelist as the campaigns page Filters
// (useAllAssistantOptions). "email" dropped here (vs. Frontend's version) — no Email
// campaign channel in this build.
const CAMPAIGN_ASSISTANT_TYPES = ["sms", "voice", "whatsapp"];

function getReportMinDate() {
  return dayjs().subtract(REPORT_LOOKBACK_DAYS, "day").startOf("day");
}

/**
 * "Download Report" button + options dialog for the Single Campaigns page.
 * The report is built in the background and emailed as a 24-hour download link.
 */
export default function CampaignReportDownload(props) {
  const { projectId, assistants } = props;

  // One assistant per report: campaigns from different assistants carry different
  // extraction fields, so mixing them makes the sheet unreadable.
  const assistantOptions = useMemo(
    () =>
      (assistants || [])
        .filter((assistant) =>
          CAMPAIGN_ASSISTANT_TYPES.includes(assistant.assistantType),
        )
        .map((assistant) => ({ label: assistant.name, value: assistant._id })),
    [assistants],
  );

  const [isOpen, setIsOpen] = useState(false);
  const [assistantId, setAssistantId] = useState(undefined);
  // [start, end] as dayjs values from the range picker, or null until both are picked.
  const [dateRange, setDateRange] = useState(null);
  // Multi-select filters: an empty list means "all".
  const [callStatuses, setCallStatuses] = useState([]);
  const [channels, setChannels] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const hasDateRange = Boolean(dateRange?.[0] && dateRange?.[1]);

  const canRequest = Boolean(assistantId) && hasDateRange;

  const reset = () => {
    setIsOpen(false);
    setAssistantId(undefined);
    setDateRange(null);
    setCallStatuses([]);
    setChannels([]);
  };

  const onClose = () => {
    if (submitting) return;
    reset();
  };

  const requestReport = async () => {
    if (!canRequest || submitting) return;

    setSubmitting(true);
    try {
      const response = await campaignService.requestCampaignReport(projectId, {
        assistantId,
        startDate: dateRange[0].startOf("day").valueOf(),
        // End of the chosen day, so campaigns created on it are included. The backend
        // caps this at the request time when the end day is today.
        endDate: dateRange[1].endOf("day").valueOf(),
        // The days above are local to this browser; the email labels the range in the same zone.
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        ...(callStatuses.length > 0 ? { callStatuses } : {}),
        ...(channels.length > 0 ? { channels } : {}),
      });
      message.success(
        response?.data?.message ||
          "Your report is being prepared. We'll email you a download link.",
      );
      reset();
    } catch (error) {
      showErrorToast(error, "Failed to request campaign report");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        outline
        onClick={() => setIsOpen(true)}
      >
        <ArrowDownTrayIcon /> Download Report
      </Button>

      {isOpen && (
        <Dialog
          size="sm"
          open={true}
          onClose={onClose}
        >
          <DialogTitle>Download Campaign Report</DialogTitle>
          <DialogBody>
            <div className="flex flex-col items-start gap-y-8 w-full">
              <div className="flex flex-col w-full items-start gap-y-2">
                <p className="text-sm font-semibold text-gray-500">Assistant:</p>
                <Select
                  showSearch
                  optionFilterProp="label"
                  className="w-full"
                  placeholder="Select assistant"
                  options={assistantOptions}
                  value={assistantId}
                  onChange={(value) => setAssistantId(value)}
                />
              </div>
              <div className="flex flex-col w-full items-start gap-y-2">
                <p className="text-sm font-semibold text-gray-500">
                  Campaigns created between:
                </p>
                <DatePicker.RangePicker
                  className="border-slate-200 font-medium text-xl w-full"
                  variant="outlined"
                  format="YYYY-MM-DD"
                  placeholder={["Start date", "End date"]}
                  minDate={getReportMinDate()}
                  maxDate={dayjs()}
                  value={dateRange}
                  onChange={(values) => setDateRange(values)}
                />
              </div>
              <div className="flex w-full flex-col items-start gap-y-2">
                <p className="text-sm font-semibold text-gray-500">Channel:</p>
                <Select
                  mode="multiple"
                  allowClear
                  className="w-full"
                  placeholder="All channels"
                  options={REPORT_CHANNEL_OPTIONS}
                  value={channels}
                  onChange={(values) => setChannels(values)}
                />
              </div>
              <div className="flex w-full flex-col items-start gap-y-2">
                <p className="text-sm font-semibold text-gray-500">Status:</p>
                <Select
                  mode="multiple"
                  allowClear
                  className="w-full"
                  placeholder="All statuses"
                  options={REPORT_STATUS_OPTIONS}
                  value={callStatuses}
                  onChange={(values) => setCallStatuses(values)}
                />
              </div>
              <p className="text-sm text-gray-500">
                The report will be emailed to you as a download link, valid for
                24 hours.
              </p>
            </div>
          </DialogBody>
          <DialogActions>
            <Button
              outline
              disabled={submitting}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              disabled={!canRequest || submitting}
              onClick={requestReport}
            >
              {submitting ? "Requesting..." : "Request Report"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
